/* global themeVars */

/**
 * Class to handle articles filtering functionality.
 */
class ArticlesFilters {

	/**
	 * Class to handle articles filtering functionality.
	 * @param {Object} options - The configuration object containing the necessary parameters.
	 * @param {HTMLElement} options.block - The parent block element containing the filters and articles items.
	 * @param {string} options.buttonsID - The selector for the filter buttons.
	 * @param {string} options.containerClass - The CSS class for the articles container.
	 * @param {string} options.ajaxAction - The AJAX action to call.
	 * @param {string} options.pagination - The CSS class for pagination links.
	 * @param {string|null} options.arrowNextClass - The CSS class for the next arrow (optional).
	 * @param {string|null} options.arrowPrevClass - The CSS class for the previous arrow (optional).
	 */
	constructor( options = {} ) {

		if( !options.block || !options.buttonsID || !options.containerClass || !options.ajaxAction ) {

			return false;

		}

		// Directly assign options to class properties
		this.block = options.block;
		this.buttonsID = options.buttonsID;
		this.containerClass = options.containerClass;
		this.ajaxAction = options.ajaxAction;
		this.paginationPagesClass = options.pagination || '';
		this.arrowNextClass = options.arrowNextClass || null;
		this.arrowPrevClass = options.arrowPrevClass || null;


		if ( this.init() ) {

			this.addEvents();

		}

	}

	/**
	 * Initializes filter buttons and articles container.
	 * @return {boolean} True if initialization is successful, false otherwise.
	 */
	init() {

		//buttons
		this.buttons = this.block.querySelectorAll( this.buttonsID );
		if ( !this.buttons ) {

			return false;

		}

		//articles container
		this.articles_container = this.block.querySelector( this.containerClass );
		if ( !this.articles_container ) {

			return false;

		}

		this.postsCount = this.block.getAttribute( 'data-post-count' );
		if ( !this.postsCount ) {

			this.postsCount = 10;

		}

		this.taxonomy = 'category';
		this.postType = 'post'

		this.error = this.block.getAttribute( 'data-error' );
		if ( !this.error ) {

			this.error = '';

		}

		this.paginations = this.block.querySelectorAll( this.paginationPagesClass );

		return true;

	}


	/**
	 * Adds event listeners to filter buttons.
	 */
	addEvents() {

		this.buttons.forEach( button => {

			button.addEventListener( 'click', this.handleFilterClick.bind( this ) );
			button.addEventListener( 'keydown', ( event ) => {

				if ( event.key === 'Enter' || event.keyCode === 13 ) {

					this.handleFilterClick( event );

				}

			} );

		} );
		this.addPaginationEvents();
		this.addArrowsEvents();

	}

	/**
	 * Adds event listeners to pagination links.
	 */
	addPaginationEvents() {

		if( this.paginations ) {

			this.paginations.forEach( pageBtn => {

				pageBtn.addEventListener( 'click', this.handlePage.bind( this ) );
				pageBtn.addEventListener( 'keydown', ( event ) => {

					if ( event.key === 'Enter' || event.keyCode === 13 ) {

						this.handlePage( event );

					}

				} );

			} );

		}

	}

	/**
	 * Scrolls the page to the articles container.
	 */
	scrollToArticles() {

		const targetPosition = this.articles_container.getBoundingClientRect().top + window.pageYOffset - 165;
		const startPosition = window.pageYOffset;
		const distance = targetPosition - startPosition;
		const duration = 400;
		let start = null;

		/**
		 * Smoothly scrolls the window to the target position over a specified duration.
		 *
		 * @param {DOMHighResTimeStamp} timestamp - The current time from requestAnimationFrame.
		 */
		const smoothScroll = ( timestamp ) => {

			if ( !start ) {

				start = timestamp;

			}

			const progress = timestamp - start;
			const progressPercentage = Math.min( progress / duration, 1 );
			const easing = progressPercentage < 0.5
				? 2 * progressPercentage * progressPercentage
				: -1 + ( 4 - 2 * progressPercentage ) * progressPercentage;

			window.scrollTo( 0, startPosition + distance * easing );

			if ( progress < duration ) {

				window.requestAnimationFrame( smoothScroll );

			}

		};

		window.requestAnimationFrame( smoothScroll );

	}

	/**
	 * Adds event listeners to arrow links.
	 */
	addArrowsEvents() {

		if( this.arrowNextClass ) {

			const arrowNext = this.block.querySelector( `a${  this.arrowNextClass}` );
			if( arrowNext ) {

				arrowNext.addEventListener( 'click', this.handlePage.bind( this ) );
				arrowNext.addEventListener( 'keydown', ( event ) => {

					if ( event.key === 'Enter' || event.keyCode === 13 ) {

						this.handlePage( event );

					}

				} );

			}

		}
		if( this.arrowPrevClass ) {

			const arrowPrev = this.block.querySelector( `a${  this.arrowPrevClass}` );
			if( arrowPrev ) {

				arrowPrev.addEventListener( 'click', this.handlePage.bind( this ) );
				arrowPrev.addEventListener( 'keydown', ( event ) => {

					if ( event.key === 'Enter' || event.keyCode === 13 ) {

						this.handlePage( event );

					}

				} );

			}

		}

	}

	/**
	 * Handles click events on filter buttons.
	 * @param {Event} event - The click event object.
	 */
	handleFilterClick( event ) {

		event.preventDefault();
		const filterCategory = event.currentTarget.dataset.articlesFilter;
		this.activateButton( event.currentTarget );

		this.fetchArticles( filterCategory );

	}


	/**
	 * Handles click events on pagination links.
	 * @param {Event} event - The click event object.
	 */
	handlePage( event ) {

		event.preventDefault();

		const currentPage = this.getCurrentPage( event.currentTarget );
		const activeCategory = this.getActiveCategory();
		this.fetchArticles( activeCategory, currentPage );

	}

	/**
	 * Extracts current page number from the clicked pagination link.
	 * @param {HTMLElement} pageLink - The clicked pagination link element.
	 * @return {number} The current page number.
	 */
	getCurrentPage( pageLink ) {

		const pageMatch = pageLink.getAttribute( 'href' ).match( /\/page\/(\d+)/ );
		return pageMatch && pageMatch[1] ? parseInt( pageMatch[1], 10 ) : 1;

	}

	/**
	 * Retrieves the currently active category from the filter buttons.
	 * @return {string} The active category, or an empty string if none is active.
	 */
	getActiveCategory() {

		// Find the active button
		const activeButton = Array.from( this.buttons ).find( button => button.classList.contains( 'active' ) );

		// Return the active category from the button, or check the block's data-category attribute if no active button is found
		return activeButton ? activeButton.dataset.articlesFilter : this.block.dataset.category || '';

	}

	/**
	 * Adds 'active' class to the parent of the selected button.
	 * @param {HTMLElement} btn - The button whose parent will be activated.
	 */
	activateButton( btn ) {

		this.deactivateAllButtons();
		btn.classList.add( 'active' );

	}

	/**
	 * Deactivates all button parents by removing the 'active' class from them.
	 */
	deactivateAllButtons() {

		this.buttons.forEach( button => button.classList.remove( 'active' ) );

	}

	/**
	 * Fetches and displays articles based on the selected category and page number.
	 * @param {string} category - The category to filter articles by.
	 * @param {number} page - The page number to fetch.
	 */
	fetchArticles( category, page = 1 ) {

		this.articles_container.classList.add( 'is-loading' );
		const params = new URLSearchParams( {
			action: this.ajaxAction,
			security: themeVars.security,
			perPage: this.postsCount,
			taxonomy: this.taxonomy,
			postType: this.postType,
			page: page,
			baseUrl: window.location.href.replace( /\/page\/\d+/, '' ),
		} );

		// Append the category if one is selected
		if ( category ) {

			params.append( 'category', category );

		}

		fetch( themeVars.ajax_url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: params,
		} ).then( ( response ) => {

			return response.json();

		} ).then( ( response ) => {

			this.articles_container.innerHTML = response.data.html;

		} ).catch( () => {

			this.articles_container.innerHTML = `<p>Error</p>`;

		} ).finally( () => {

			this.articles_container.classList.remove( 'is-loading' );
			if( this.paginationPagesClass ) {

				this.paginations = this.block.querySelectorAll( this.paginationPagesClass );
				this.addPaginationEvents();

			}
			this.addArrowsEvents();
			this.scrollToArticles();

		} );

	}

}

window.ArticlesFilters = ArticlesFilters;
